import i18n from "../../../i18n";
import {
	useGetAssessmentQuery,
	useGetProctoringEventsQuery,
	useGetProctoringSessionQuery,
	useGetUserQuery,
} from "./store/hooks";
import { skipToken } from "@reduxjs/toolkit/query";
import styles from "./ProctoringResultsHeader.module.css";
import { Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ReactNode } from "react";
import { ProctoringEvent, User } from "./store/slices/proctoringSession";
import dayjs from "dayjs";
import TextSkeleton from "../../../utility/TextSkeleton";
import { CoreProfileImage } from "@verity/components/core";

type FlagCounts = {
	severe: number;
	major: number;
	minor: number;
};

function ProfilePicture({ user, userIsLoading }: { user?: User; userIsLoading: boolean }) {
	return (
		<CoreProfileImage
			size={60}
			src={user?.profilePictureUrl}
			name={user?.name}
			alt={i18n.t("result.components.resultsPage.proctoringResultsHeaderTSX.profilePicture")}
			isLoading={userIsLoading}
		/>
	);
}

function DetailRow({ label, children, isLoading }: { label: ReactNode; children?: ReactNode; isLoading?: boolean }) {
	return (
		<div className={styles.detailRow}>
			<span className={styles.infoLabel}>{label}: </span>
			{isLoading ? <TextSkeleton /> : children}
		</div>
	);
}

function computeFlagsSummary(events: ProctoringEvent[] = []): FlagCounts {
	const flagCounts: [number, number, number] = [0, 0, 0];
	events.forEach(({ anomaly }) => {
		if (anomaly) {
			const flagLevel = anomaly.flagLevel;
			if (flagLevel) {
				flagCounts[flagLevel - 1] += 1;
			}
		}
	});
	const [minor, major, severe] = flagCounts;
	return {
		severe,
		major,
		minor,
	};
}

function FlagPieChart({ flagCounts }: { flagCounts: FlagCounts }) {
	const { severe, major, minor } = flagCounts;

	let maxFlagTxt = i18n.t("result.components.studentSelection.selectionMenuTSX.none");
	if (severe > 0) {
		maxFlagTxt = i18n.t("result.components.studentSelection.selectionMenuTSX.severe");
	} else if (major > 0) {
		maxFlagTxt = i18n.t("result.components.studentSelection.selectionMenuTSX.major");
	} else if (minor > 0) {
		maxFlagTxt = i18n.t("result.components.studentSelection.selectionMenuTSX.minor");
	}

	const circleRadius = 50;
	const circumference = 2 * Math.PI * circleRadius;
	const totalCount = severe + major + minor;
	const severeArcLength = ((severe / totalCount) * circumference).toFixed(2);
	const severeDasharray = "" + severeArcLength + ", " + circumference.toFixed(2);
	const majorArcLength = ((major / totalCount) * circumference).toFixed(2);
	const majorDasharray = "" + majorArcLength + ", " + circumference.toFixed(2);
	const minorDasharray = "" + ((minor / totalCount) * circumference).toFixed(2) + ", " + circumference.toFixed(2);

	const severeOffset = "0";
	const majorOffset = severe === 0 ? "0" : "-" + severeArcLength;
	const minorOffset =
		severe === 0 && major === 0 ? "0" : "-" + (Number(severeArcLength) + Number(majorArcLength)).toFixed(2);

	return (
		<div style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
			<svg width="67" height="67" viewBox="16 15 115 116">
				<circle cx="75" cy="75" r={circleRadius} stroke="#AAA" strokeWidth="10px" fill="transparent" />
				{severe > 0 && (
					<circle
						cx="75"
						cy="75"
						r={circleRadius}
						stroke="#D15127"
						strokeWidth="10px"
						strokeDasharray={severeDasharray}
						strokeDashoffset={severeOffset}
						fill="transparent"
						transform="rotate(-90, 75, 75)"
					/>
				)}

				{major > 0 && (
					<circle
						cx="75"
						cy="75"
						r={circleRadius}
						stroke="#EFC65C"
						strokeWidth="10px"
						strokeDasharray={majorDasharray}
						strokeDashoffset={majorOffset}
						fill="transparent"
						transform="rotate(-90, 75, 75)"
					/>
				)}

				{minor > 0 && (
					<circle
						cx="75"
						cy="75"
						r={circleRadius}
						stroke="#2E620E"
						strokeWidth="10px"
						strokeDasharray={minorDasharray}
						strokeDashoffset={minorOffset}
						fill="transparent"
						transform="rotate(-90, 75, 75)"
					/>
				)}
			</svg>
			<span
				style={{
					position: "absolute",
					fontSize: "14px",
					fontWeight: "bold",
					textAlign: "center",
				}}
			>
				{maxFlagTxt}
			</span>
		</div>
	);
}

function FlagCount({
	severity,
	count,
	children,
	isLoading,
}: {
	severity: keyof FlagCounts | "total";
	count: number;
	children: ReactNode;
	isLoading?: boolean;
}) {
	return (
		<div className={styles.flagCountWrapper}>
			{isLoading ? (
				<Skeleton variant="circular" width={67} height={67} />
			) : (
				<div className={`${styles.flagCount} ${styles[severity]}`}>{count}</div>
			)}
			{children}
		</div>
	);
}

export default function ProctoringResultsHeader() {
	const { data: proctoringSession, isLoading: sessionIsLoading } = useGetProctoringSessionQuery();
	const { data: assessment, isLoading: assessmentQueryIsLoading } = useGetAssessmentQuery(
		proctoringSession?.lmsQuizId ?? skipToken,
	);
	const { data: user, isLoading: userQueryIsLoading } = useGetUserQuery(proctoringSession?.studentId ?? skipToken);
	const { data: proctoringEventInfo, isLoading: proctoringEventsLoading } = useGetProctoringEventsQuery();

	const theme = useTheme();
	const largeWidth = useMediaQuery(theme.breakpoints.down("lg"), {
		matchMedia: (query) => {
			return window.matchMedia(query);
		},
	});
	const mediumWidth = useMediaQuery(theme.breakpoints.down("md"), {
		matchMedia: (query) => {
			return window.matchMedia(query);
		},
	});

	const flagCounts = computeFlagsSummary(proctoringEventInfo?.events);

	const assessmentIsLoading = sessionIsLoading || assessmentQueryIsLoading;
	const userIsLoading = sessionIsLoading || userQueryIsLoading;

	return (
		<div className={styles.resultsHeader}>
			<div className={styles.sessionDetailsArea}>
				<ProfilePicture user={user} userIsLoading={userIsLoading} />
				<div className={styles.sessionInfo}>
					<Typography variant="h3" className={styles.userName}>
						{userIsLoading ? <TextSkeleton /> : user?.name}
					</Typography>
					<DetailRow
						label={i18n.t("result.components.resultsPage.proctoringResultsHeaderTSX.course")}
						isLoading={assessmentIsLoading}
					>
						{assessment?.course?.name}
					</DetailRow>
					<DetailRow
						label={i18n.t("result.components.resultsPage.proctoringResultsHeaderTSX.assessment")}
						isLoading={assessmentIsLoading}
					>
						{assessment?.name}
					</DetailRow>
					<DetailRow
						label={i18n.t("result.components.resultsPage.proctoringResultsHeaderTSX.startDate")}
						isLoading={sessionIsLoading}
					>
						{proctoringSession ? dayjs(proctoringSession.createdAt).format("DD/MM/YYYY, hh:mm:ss A") : null}
					</DetailRow>
				</div>
			</div>
			<div className={styles.flagCircles}>
				<div className={styles.flagCountWrapper}>
					{proctoringEventsLoading ? (
						<Skeleton variant="circular" width={67} height={67} />
					) : (
						<FlagPieChart flagCounts={flagCounts} />
					)}
					{i18n.t("result.components.resultsPage.proctoringResultsHeaderTSX.overall")}
				</div>
				{!mediumWidth && (
					<>
						<FlagCount severity="severe" count={flagCounts.severe} isLoading={proctoringEventsLoading}>
							{i18n.t("result.components.resultsPage.proctoringResultsHeaderTSX.severe")}
						</FlagCount>
						<FlagCount severity="major" count={flagCounts.major} isLoading={proctoringEventsLoading}>
							{i18n.t("result.components.resultsPage.proctoringResultsHeaderTSX.major")}
						</FlagCount>
						<FlagCount severity="minor" count={flagCounts.minor} isLoading={proctoringEventsLoading}>
							{i18n.t("result.components.resultsPage.proctoringResultsHeaderTSX.minor")}
						</FlagCount>
					</>
				)}
				{!largeWidth && (
					<FlagCount
						severity="total"
						count={flagCounts.severe + flagCounts.major + flagCounts.minor}
						isLoading={proctoringEventsLoading}
					>
						{i18n.t("result.components.resultsPage.proctoringResultsHeaderTSX.flags")}
					</FlagCount>
				)}
			</div>
		</div>
	);
}
